import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getUserPermissions, hasPagePermissionWithAdmin } from './PermissionService';
import Swal from 'sweetalert2';
import { cleanupStockSelectionLocks } from '../Api/GmsStockSelectionLock';

const PermissionGuard = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [isAuthError, setIsAuthError] = useState(false);
  const location = useLocation();

  // Cleanup old stock locks on app initialization (once per session)
  useEffect(() => {
    const cleanupOldLocks = async () => {
      try {
        // Clean up locks older than 24 hours (1 day) on app initialization
        // This removes stale locks from previous sessions
        await cleanupStockSelectionLocks(24).catch(err => {
          console.warn('Failed to cleanup old stock locks on app init:', err);
          // Don't block app initialization if cleanup fails
        });
      } catch (error) {
        console.warn('Error during stock lock cleanup on app init:', error);
        // Don't block app initialization if cleanup fails
      }
    };
    
    // Only cleanup if user is authenticated
    const hasToken = localStorage.getItem("token") || localStorage.getItem("authUser");
    if (hasToken) {
      cleanupOldLocks();
    }
  }, []); // Run only once on mount

  useEffect(() => {
    const checkPermission = async () => {
      setIsAuthError(false); // Reset auth error flag on each permission check
      try {
        const permissions = await getUserPermissions();
        
        // Store permissions in localStorage so spa pages can access them
        localStorage.setItem('permissions', JSON.stringify(permissions));
        
        const isAllowed = hasPagePermissionWithAdmin(permissions, location.pathname);
        
        // Special handling for Dashboard - check if user has other dashboard permissions
        if (!isAllowed && location.pathname === '/Dashboard') {
          const hasSpaManagerFinancePermission = hasPagePermissionWithAdmin(permissions, '/GmsSpaManagerFinanceDashboard');
          const hasSpaDashboardPermission = hasPagePermissionWithAdmin(permissions, '/GmsSpaEmployeeDashboard');
          if (hasSpaManagerFinancePermission) {
            // Redirect to Spa Manager & Finance Dashboard without showing alert
            setHasPermission(false);
            return;
          } else if (hasSpaDashboardPermission) {
            // Redirect to Spa Employee Dashboard without showing alert
            setHasPermission(false);
            return;
          } else {
            // Redirect to Default Dashboard without showing alert
            setHasPermission(false);
            return;
          }
        }
        
        if (!isAllowed) {
          Swal.fire({
            title: "Access Denied",
            text: "You don't have permission to access this page",
            icon: "error"
          });
        }
        
        setHasPermission(isAllowed);
      } catch (error) {
        console.error("Error checking permissions:", error);
        
        // Check if error is authentication-related (401 or missing auth)
        const isAuthError = error?.response?.status === 401 || 
                           error?.message?.includes('not logged in') ||
                           error?.message?.includes('User ID not found') ||
                           !localStorage.getItem("token") ||
                           !localStorage.getItem("authUser");
        
        if (isAuthError) {
          // Session expired or invalid - clear permissions and flag for redirect
          localStorage.removeItem('permissions');
          setIsAuthError(true);
          setLoading(false);
          return;
        }
        
        setHasPermission(false);
      } finally {
        setLoading(false);
      }
    };

    checkPermission();
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // If auth error detected or no permission and no token, redirect to login (session expired)
  const hasToken = localStorage.getItem("token") || localStorage.getItem("authUser");
  if (isAuthError || (!hasPermission && !hasToken)) {
    return <Navigate to="/login" replace />;
  }

  if (hasPermission) {
    return children;
  }

  // If user doesn't have permission for the requested page, 
  // try to redirect to appropriate dashboard
  // Only use stored permissions if we have a valid token (prevent using stale data)
  const permissions = hasToken ? JSON.parse(localStorage.getItem('permissions') || '[]') : [];
  const hasDashboardPermission = hasPagePermissionWithAdmin(permissions, '/Dashboard');
  const hasSpaManagerFinancePermission = hasPagePermissionWithAdmin(permissions, '/GmsSpaManagerFinanceDashboard');
  const hasSpaDashboardPermission = hasPagePermissionWithAdmin(permissions, '/GmsSpaEmployeeDashboard');
  
  // Special handling for Dashboard route
  if (location.pathname === '/Dashboard') {
    if (hasSpaManagerFinancePermission) {
      return <Navigate to="/GmsSpaManagerFinanceDashboard" replace />;
    } else if (hasSpaDashboardPermission) {
      return <Navigate to="/GmsSpaEmployeeDashboard" replace />;
    } else {
      return <Navigate to="/DefaultDashboard" replace />; // Redirect to default dashboard
    }
  }
  
  if (hasDashboardPermission) {
    return <Navigate to="/Dashboard" replace />;
  } else if (hasSpaManagerFinancePermission) {
    return <Navigate to="/GmsSpaManagerFinanceDashboard" replace />;
  } else if (hasSpaDashboardPermission) {
    return <Navigate to="/GmsSpaEmployeeDashboard" replace />;
  } else {
    return <Navigate to="/DefaultDashboard" replace />; // Redirect to default dashboard
  }
};

export default PermissionGuard; 